import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cocktailApi, Cocktail } from '../services/cocktailApi.ts';
import { FavoritesService } from '../services/favoritesService.ts';
import './Favorites.css';

const Favorites: React.FC = () => {
  const [favorites, setFavorites] = useState<Cocktail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to sort cocktails alphabetically by drink name
  const sortCocktailsAlphabetically = (cocktails: Cocktail[]): Cocktail[] => {
    return [...cocktails].sort((a, b) => {
      const nameA = (a.strDrink || '').toLowerCase();
      const nameB = (b.strDrink || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    setLoading(true);
    setError(null);

    try {
      const favoriteIds = FavoritesService.getFavorites();
      if (favoriteIds.length === 0) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      // Fetch cocktail details for each favorite ID
      const favoriteCocktails: Cocktail[] = [];

      for (const id of favoriteIds) {
        try {
          const cocktail = await cocktailApi.getCocktailById(id);
          if (cocktail) {
            favoriteCocktails.push(cocktail);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error(`Error loading cocktail ${id}:`, error);
          }
          // Continue with other favorites even if one fails
        }
      }

      // Sort favorites alphabetically before setting
      setFavorites(sortCocktailsAlphabetically(favoriteCocktails));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading favorites:', error);
      }
      setError('Failed to load favorites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = (cocktailId: string) => {
    FavoritesService.removeFavorite(cocktailId);
    // Remove from local state immediately for better UX and maintain alphabetical order
    setFavorites(prev => sortCocktailsAlphabetically(prev.filter(cocktail => cocktail.idDrink !== cocktailId)));
  };

  const handleClearAllFavorites = () => {
    if (window.confirm('Are you sure you want to remove all favorites?')) {
      FavoritesService.clearFavorites();
      setFavorites([]);
    }
  };

  if (loading) {
    return (
      <div className="favorites">
        <h1>My Favorite Cocktails</h1>
        <div className="loading">Loading your favorites...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="favorites">
        <h1>My Favorite Cocktails</h1>
        <div className="error">{error}</div>
        <button onClick={loadFavorites} className="retry-btn">Try Again</button>
      </div>
    );
  }

  return (
    <div className="favorites">
      <div className="favorites-header">
        <h1>My Favorite Cocktails</h1>
        {favorites.length > 0 && (
          <div className="favorites-actions">
            <span className="favorites-count">{favorites.length} favorite{favorites.length !== 1 ? 's' : ''}</span>
            <button onClick={handleClearAllFavorites} className="clear-all-btn">
              Clear All
            </button>
          </div>
        )}
      </div>

      {favorites.length === 0 ? (
        <div className="no-favorites">
          <div className="no-favorites-icon">❤️</div>
          <h2>No favorites yet</h2>
          <p>Start exploring cocktails and add your favorites to see them here!</p>
          <div className="no-favorites-actions">
            <Link to="/browse" className="browse-btn">Browse Cocktails</Link>
            <Link to="/" className="home-btn">Go Home</Link>
          </div>
        </div>
      ) : (
        <div className="favorites-grid">
          {favorites.map(cocktail => (
            <div key={cocktail.idDrink} className="cocktail-card favorite-card">
              <Link to={`/cocktail/${cocktail.idDrink}`} className="cocktail-image-link">
                <img
                  src={cocktail.strDrinkThumb}
                  alt={cocktail.strDrink}
                  className="cocktail-image"
                />
              </Link>
              <div className="cocktail-info">
                <h3 className="cocktail-name">{cocktail.strDrink}</h3>
                {cocktail.strCategory && (
                  <p className="cocktail-category">{cocktail.strCategory}</p>
                )}
                {cocktail.strAlcoholic && (
                  <p className="cocktail-alcoholic">{cocktail.strAlcoholic}</p>
                )}
                <div className="card-actions">
                  <Link to={`/cocktail/${cocktail.idDrink}`} className="view-recipe-btn">
                    View Recipe
                  </Link>
                  <button
                    onClick={() => handleRemoveFavorite(cocktail.idDrink)}
                    className="remove-favorite-btn"
                    title="Remove from favorites"
                  >
                    💔 Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Favorites;



